import { useState, useEffect } from "react";
import CaseStudyStrip from "@/components/CaseStudyStrip";
import CaseStudyModal from "@/components/CaseStudyModal";
import { caseStudies } from "@/data/caseStudies";
import type { CaseStudy } from "@/components/CaseStudyStrip";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
  type CarouselApi,
} from "@/components/ui/carousel";
import { cn } from "@/lib/utils";

const CaseStudiesCarousel = () => {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [selectedCaseStudy, setSelectedCaseStudy] = useState<CaseStudy | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (!api) return;

    setCurrent(api.selectedScrollSnap());

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);

  // Autoplay effect
  useEffect(() => {
    if (!api || isHovered) return;

    const interval = setInterval(() => {
      api.scrollNext();
    }, 5000);

    return () => clearInterval(interval);
  }, [api, isHovered]);

  const handleStripClick = (study: CaseStudy) => {
    setSelectedCaseStudy(study);
    setModalOpen(true);
  };

  return (
    <div 
      className="max-w-5xl mx-auto space-y-6"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Header eliminado - el título viene de Index.tsx */}

      {/* Carousel de tiras */}
      <Carousel
        setApi={setApi}
        opts={{
          align: "start",
          loop: true,
        }}
        className="w-full"
      >
        <CarouselContent className="-ml-2 md:-ml-4">
          {caseStudies.map((study) => (
            <CarouselItem
              key={study.id}
              className="pl-2 md:pl-4 basis-full md:basis-1/2"
            >
              <CaseStudyStrip 
                caseStudy={study} 
                onClick={() => handleStripClick(study)}
              />
            </CarouselItem>
          ))}
        </CarouselContent>

        {/* Controles */}
        <CarouselPrevious className="hidden sm:flex -left-12" />
        <CarouselNext className="hidden sm:flex -right-12" />
      </Carousel>

      {/* Indicadores de puntos minimalistas */}
      <div className="flex justify-center gap-2 pt-4">
        {caseStudies.map((_, index) => (
          <button
            key={index}
            onClick={() => api?.scrollTo(index)}
            className={cn(
              "h-1.5 rounded-full transition-all duration-300",
              current === index
                ? "w-8 bg-foreground"
                : "w-1.5 bg-muted-foreground/20 hover:bg-muted-foreground/40"
            )}
            aria-label={`Ir al caso de éxito ${index + 1}`}
          />
        ))}
      </div>

      {/* Modal de detalles */}
      <CaseStudyModal
        caseStudy={selectedCaseStudy}
        open={modalOpen}
        onOpenChange={setModalOpen}
      />
    </div>
  );
};

export default CaseStudiesCarousel;
